import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, CalendarDays, ShoppingCart, Clock, FileText, MessageSquareWarning, Wallet, Receipt } from "lucide-react";
import { http, formatSoles, statusLabel } from "@/api";
import { PageHeader, Panel, Pill, ORDER_STATUS_COLORS, fmtDateTime } from "./ui";

function Stat({ icon: Icon, label, value, to, accent, testid }) {
  const body = (
    <div className="bg-white rounded-2xl border border-black/5 p-5 h-full hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{label}</span>
        <Icon size={18} className={accent ? "text-[var(--brand-primary)]" : "text-[var(--brand-accent)]"} />
      </div>
      <div className="font-serif text-3xl font-bold mt-3">{value}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function DashboardPage() {
  const [s, setS] = useState(null);
  useEffect(() => { http.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-neutral-500">Cargando resumen…</div>;
  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Resumen" subtitle="Estado general de tu negocio" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={TrendingUp} label="Ventas del día" value={formatSoles(s.sales_today)} accent testid="stat-sales-today" />
        <Stat icon={CalendarDays} label="Ventas del mes" value={formatSoles(s.sales_month)} accent testid="stat-sales-month" />
        <Stat icon={ShoppingCart} label="Pedidos nuevos (7 días)" value={s.orders_new} to="/admin/pedidos" testid="stat-orders-new" />
        <Stat icon={Clock} label="Pedidos pendientes" value={s.orders_pending} to="/admin/pedidos" testid="stat-orders-pending" />
        <Stat icon={FileText} label="Cotizaciones recibidas" value={s.quotes_total} to="/admin/cotizaciones" testid="stat-quotes-total" />
        <Stat icon={MessageSquareWarning} label="Cotizaciones por responder" value={s.quotes_pending} to="/admin/cotizaciones" testid="stat-quotes-pending" />
        <Stat icon={Wallet} label="Pagos pendientes" value={s.payments_pending} to="/admin/pedidos" testid="stat-payments-pending" />
        <Stat icon={Receipt} label="Comprobantes por verificar" value={s.receipts_to_verify} to="/admin/pedidos" testid="stat-receipts" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <Panel title="Últimos pedidos" actions={<Link to="/admin/pedidos" className="text-sm text-[var(--brand-primary)] font-semibold">Ver todos</Link>}>
          {s.recent_orders.length === 0 && <p className="text-sm text-neutral-500">Aún no hay pedidos.</p>}
          <div className="divide-y">
            {s.recent_orders.map((o) => (
              <Link key={o.id} to={`/admin/pedidos/${o.id}`} className="flex items-center justify-between py-3 hover:bg-neutral-50 px-1 rounded">
                <div>
                  <div className="font-semibold text-[var(--brand-primary)]">{o.order_code}</div>
                  <div className="text-xs text-neutral-500">{o.customer_name} · {fmtDateTime(o.created_at)}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{formatSoles(o.total)}</div>
                  <Pill className={ORDER_STATUS_COLORS[o.status]}>{statusLabel(o.status)}</Pill>
                </div>
              </Link>
            ))}
          </div>
        </Panel>
        <Panel title="Últimos movimientos" actions={<Link to="/admin/historial" className="text-sm text-[var(--brand-primary)] font-semibold">Historial</Link>}>
          {s.movements.length === 0 && <p className="text-sm text-neutral-500">Sin movimientos todavía.</p>}
          <ul className="space-y-2" data-testid="movements-list">
            {s.movements.map((m) => (
              <li key={m.id} className="flex items-start justify-between gap-3 text-sm">
                <span><b>{m.action}</b> {m.entity}{m.label ? `: ${m.label}` : ""}{m.actor === "cliente" ? " · cliente" : ""}</span>
                <span className="text-xs text-neutral-400 whitespace-nowrap">{fmtDateTime(m.created_at)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
